/**
 * AppTopBar — barra superiore sticky.
 *
 * Layout:
 * - Sinistra: logo (mobile only, md:hidden) + titolo pagina corrente
 * - Centro:   ricerca globale pazienti (PR-SEARCH-HEADER). Superata la
 *             decisione PR3 D4 (campo disabled + tooltip "Disponibile
 *             quando avrai i primi pazienti"). Montata solo per
 *             TITOLARE, DIETOLOGA, SEGRETERIA. ADMIN e PAZIENTE: slot
 *             vuoto (le rules Firestore negano list su /pazienti).
 * - Destra:   MfaFreshnessPill + notifiche (disabled) + help (disabled)
 *             + UserMenuDropdown
 *
 * Mobile: logo-button (44px, logout immediato via confirmLogout) a
 * sinistra del titolo. Sul desktop il logo vive già in AppSidebar: il
 * logo mobile è md:hidden per non duplicarlo. L'icona search (44px)
 * apre lo stesso combobox a larghezza header.
 *
 * Sticky top + backdrop-blur per coerenza con design.md §9.3.
 */

import { MfaFreshnessPill } from './MfaFreshnessPill';
import { UserMenuDropdown } from './UserMenuDropdown';
import { Icon } from '@shared/components/ui/Icon';
import { usePageTitleValue } from '../../hooks/usePageTitle';
import { useLogoutConfirm } from '../../hooks/useLogoutConfirm';
import { LogoutConfirmDialog } from './LogoutConfirmDialog';
import { useAuth } from '../../hooks/useAuth';
import { GlobalPazienteSearchHost } from './GlobalPazienteSearchHost';

function canUseGlobalPazienteSearch(role: string | null): boolean {
  return role === 'TITOLARE' || role === 'DIETOLOGA' || role === 'SEGRETERIA';
}

export function AppTopBar() {
  const title = usePageTitleValue();
  const { role } = useAuth();
  const { confirmOpen, confirmLogout, cancelLogout } = useLogoutConfirm();
  const showSearch = canUseGlobalPazienteSearch(role);

  return (
    <header className="app-shell-header sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-border-gray">
      <div className="h-16 px-4 md:px-6 flex items-center gap-3 md:gap-6">
        {/* Logo mobile — md:hidden (desktop ha già il logo in AppSidebar).
            Logout immediato (bypass dialog); touch target 44×44px. */}
        <button
          type="button"
          onClick={() => void confirmLogout()}
          aria-label="Esci"
          title="Esci"
          className="md:hidden flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center hover:bg-bg-app transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-magenta-soft"
        >
          <div
            aria-hidden
            className="w-9 h-9 rounded-full bg-primary flex items-center justify-center"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 40 40"
              aria-hidden="true"
            >
              <path
                d="M5 20C5 20 10 15 20 20C30 25 35 20 35 20"
                stroke="white"
                strokeLinecap="round"
                strokeWidth="3"
              />
            </svg>
          </div>
        </button>

        {/* Titolo pagina */}
        <h1
          className="font-headline-md text-headline-md text-text-main truncate min-w-0"
          aria-live="polite"
        >
          {title || ' '}
        </h1>

        {showSearch ? <GlobalPazienteSearchHost /> : <div className="flex-1 md:hidden" />}

        {/* Cluster destro */}
        <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
          {/* MFA freshness (gestisce solo staff, internamente) */}
          <div className="hidden md:block">
            <MfaFreshnessPill />
          </div>

          {/* Notifiche (placeholder disabled) */}
          <button
            type="button"
            disabled
            aria-disabled
            aria-label="Notifiche (disponibile presto)"
            title="Disponibile in una prossima versione"
            className="w-11 h-11 rounded-full flex items-center justify-center text-text-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon name="notifications" size={22} />
          </button>

          {/* Help (placeholder disabled, desktop only) */}
          <button
            type="button"
            disabled
            aria-disabled
            aria-label="Aiuto (disponibile presto)"
            title="Disponibile in una prossima versione"
            className="hidden md:flex w-11 h-11 rounded-full items-center justify-center text-text-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon name="help" size={22} />
          </button>

          {/* User menu — visibile su tutti i breakpoint. */}
          <UserMenuDropdown />
        </div>
      </div>

      <LogoutConfirmDialog
        open={confirmOpen}
        onConfirm={() => void confirmLogout()}
        onCancel={cancelLogout}
      />
    </header>
  );
}
